/**
 * SocialPulse Member Page — Phase A of the personal digital hub.
 *
 * Route: `/{handle}` (bare — matches the mockup socialpulse.com/tomvellaringattu).
 * The three-column layout matches the approved mockup precisely:
 *   • Left rail: My Pages nav + My Network
 *   • Center:    Hero (cover / avatar / verified name / stats / QR),
 *                composer stub, My Pulse feed
 *   • Right:     Social Capital tile, Top Badges (empty state),
 *                My Links, Marketplace, Upcoming Events
 *
 * Wires live for Phase A:
 *   • Identity (name, handle, cover, avatar, bio, location, verification)
 *   • Stats (followers / following / posts / social capital / level)
 *   • Social Capital tile — reads legacy `users.social_capital` via
 *     the aggregate `/api/member/:handle`
 *   • Marketplace tile — real `local_listings` count + first 6 items
 *   • Upcoming events tile — real `events` filtered to owner
 *   • QR panel — auto-mints SpLink `splink.pro/{handle}` for owners
 *   • Follow / Unfollow (POST/DELETE `/api/follow/:handle`)
 *   • Add Friend / Cancel / Accept / Decline / Unfriend (`/api/friends/*`)
 *
 * Stubbed (Phase B):
 *   • Composer submit, Pulse feed items, PulseCast distribution picker
 *   • Message, Share, Block / Report menus
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import {
  MapPin, Link as LinkIcon, Camera, CheckCircle2, Share2, Copy,
  UserPlus, UserCheck, Users, MessageCircle,
  Loader2, Award, TrendingUp, ChevronRight, ExternalLink, Plus,
  Newspaper, Video as VideoIcon, Image as ImageIcon, Calendar,
  BarChart2, Sparkles, Store, Handshake,
} from "lucide-react";

import PulseComposer from "@/components/pulse/PulseComposer";
import PulseFeed from "@/components/pulse/PulseFeed";
import SafetyMoreMenu from "@/components/safety/SafetyMoreMenu";
import ProfileArticlesSection from "@/components/publisher/ProfileArticlesSection";
import HeroLatestArticle from "@/components/publisher/HeroLatestArticle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_PURPLE = "#7C3AED";
const BRAND_BLUE   = "#3B82F6";
const BRAND_GREEN  = "#10B981";

const LEFT_NAV = [
  { key: "pulse",       label: "My Pulse",     icon: TrendingUp, active: true },
  { key: "about",       label: "About",         icon: Users },
  { key: "photos",      label: "Photos",        icon: ImageIcon },
  { key: "articles",    label: "Articles",      icon: Newspaper },
  { key: "resume",      label: "Resume",        icon: Award },
  { key: "portfolio",   label: "Portfolio",     icon: Sparkles },
  { key: "marketplace", label: "Marketplace",   icon: Store },
  { key: "services",    label: "Services",      icon: Handshake },
  { key: "events",      label: "Events",        icon: Calendar },
  { key: "groups",      label: "Groups",        icon: Users },
  { key: "connections", label: "Connections",   icon: Users },
  { key: "causes",      label: "Causes",        icon: Sparkles },
  { key: "saved",       label: "Saved",         icon: Award },
  { key: "analytics",   label: "Analytics",     icon: BarChart2 },
];

const NETWORK = [
  { slug: "nature",      label: "NaturePulse",    tagline: "Observe Nature",    color: "#10B981", to: "/nature" },
  { slug: "farmers",     label: "FarmersPulse",   tagline: "Smart Farming",     color: "#F59E0B", to: "/hire" },
  { slug: "food",        label: "FoodPulse",      tagline: "Reduce Food Waste", color: "#EF4444", to: "/food" },
  { slug: "alumni",      label: "AlumniPulse",    tagline: "Stay Connected",    color: "#7C3AED", to: "/alumni" },
  { slug: "photo",       label: "PhotoPulse",     tagline: "Share Moments",     color: "#EC4899", to: "/photo" },
  { slug: "traveler",    label: "TravelerPulse",  tagline: "Explore Places",    color: "#0EA5E9", to: "/travel" },
  { slug: "sellnbuy",    label: "SellnBuy",       tagline: "Buy · Sell · Donate", color: "#1F6FEB", to: "/local" },
];


export default function MemberProfilePage() {
  const { handle } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "pulse";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [feedToken, setFeedToken] = useState(0);

  const setActiveTab = useCallback((key) => {
    if (key === "pulse") {
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: false });
    } else {
      searchParams.set("tab", key);
      setSearchParams(searchParams, { replace: false });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/member/${handle}`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.status === 404 ? "Profile not found." : "Could not load profile.");
    } finally {
      setLoading(false);
    }
  }, [handle]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  // Mint short URL on demand for owners who don't have one cached.
  useEffect(() => {
    if (!data?.is_owner || data.short_url) return;
    axios.post(`${API}/member/short-url`, {}, { withCredentials: true })
      .then((r) => setData((d) => ({ ...d, short_url: r.data.short_url })))
      .catch(() => { /* graceful fallback: canonical URL stays */ });
  }, [data?.is_owner, data?.short_url]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center opacity-70">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-16 text-center">
        <h1 className="font-display text-3xl mb-3" style={{ color: "#0F172A" }}>
          {err || "Profile not found"}
        </h1>
        <button
          data-testid="profile-back-btn"
          onClick={() => nav("/")}
          className="wofy-btn-primary mt-4"
        >Back to home</button>
      </div>
    );
  }

  return (
    <div
      data-testid="member-profile-page"
      className="min-h-screen"
      style={{ background: "#F8FAFC" }}
    >
      <Helmet>
        <title>{`${data.identity.name || "@" + data.handle} · SocialPulse`}</title>
        <meta name="description"
              content={data.identity.about || data.identity.intro
                        || `${data.identity.name || data.handle} on SocialPulse.`} />
      </Helmet>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-5 grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)_320px]">
        <LeftRail data={data} activeTab={activeTab} onSelectTab={setActiveTab} />
        <main className="min-w-0 space-y-5">
          <Hero data={data} onChange={load} />
          {activeTab === "pulse" && (
            <>
              {data.is_owner && (
                <PulseComposer
                  me={{
                    handle: data.handle,
                    name: data.identity.name,
                    avatar_url: data.identity.avatar_url || data.identity.profile_picture,
                  }}
                  onPublished={() => setFeedToken((t) => t + 1)}
                />
              )}
              {/* Articles preview strip — prominently placed above the pulse feed */}
              {data.identity?.id && (
                <ProfileArticlesSection
                  authorId={data.identity.id}
                  isOwner={data.is_owner}
                  handle={data.handle}
                  variant="strip"
                />
              )}
              <PulseFeed handle={data.handle} isOwner={data.is_owner}
                         refreshToken={feedToken} />
            </>
          )}
          {activeTab === "articles" && data.identity?.id && (
            <ProfileArticlesSection
              authorId={data.identity.id}
              isOwner={data.is_owner}
              handle={data.handle}
              variant="grid"
              limit={50}
            />
          )}
          {activeTab !== "pulse" && activeTab !== "articles" && (
            <div className="rounded-xl bg-white border p-6 text-center text-sm text-slate-500"
                 style={{ borderColor: "#E5E7EB" }}
                 data-testid={`profile-tab-placeholder-${activeTab}`}>
              <div className="font-semibold text-slate-800 capitalize mb-1">{activeTab}</div>
              This section is on the roadmap — check back soon.
            </div>
          )}
        </main>
        <RightRail data={data} />
      </div>

      <QuickActionBar isOwner={data.is_owner} />
    </div>
  );
}


// ─── Left rail ─────────────────────────────────────────────────────

function LeftRail({ data, activeTab, onSelectTab }) {
  const avatar = data.identity.avatar_url || data.identity.profile_picture;
  return (
    <aside data-testid="member-left-rail" className="hidden lg:block">
      <div className="rounded-xl bg-white border p-4 mb-3"
           style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center gap-3">
          <img
            src={avatar || "https://api.dicebear.com/7.x/initials/svg?seed=" + data.handle}
            alt={data.identity.name}
            className="w-11 h-11 rounded-full object-cover"
          />
          <div className="min-w-0">
            <div className="font-semibold text-[14px] truncate"
                 style={{ color: "#0F172A" }}>{data.identity.name}</div>
            <div className="text-[11.5px] opacity-70 truncate">@{data.handle}</div>
          </div>
        </div>
      </div>

      <nav className="rounded-xl bg-white border p-2"
           style={{ borderColor: "#E5E7EB" }}>
        {LEFT_NAV.map((n) => {
          const badge = n.key === "marketplace" ? data.marketplace.active_count : null;
          const isActive = activeTab === n.key;
          return (
            <button
              key={n.key}
              onClick={() => onSelectTab(n.key)}
              data-testid={`nav-${n.key}`}
              aria-current={isActive ? "page" : undefined}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition"
              style={{
                background: isActive ? "rgba(124,58,237,0.10)" : "transparent",
                color:      isActive ? BRAND_PURPLE : "#334155",
                fontWeight: isActive ? 600 : 400,
              }}
            >
              <n.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{n.label}</span>
              {badge > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full"
                      style={{ background: BRAND_PURPLE, color: "white" }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="mt-4 px-1 text-[11px] font-mono uppercase tracking-[0.15em] opacity-60">
        My Network
      </div>
      <div className="rounded-xl bg-white border p-2 mt-2"
           style={{ borderColor: "#E5E7EB" }}>
        {NETWORK.map((n) => (
          <Link
            key={n.slug}
            to={n.to}
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] hover:bg-slate-50"
            style={{ color: "#334155" }}
          >
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-md text-[11px] font-mono text-white"
              style={{ background: n.color }}
            >
              {n.label[0]}
            </span>
            <div className="min-w-0">
              <div className="font-medium truncate">{n.label}</div>
              <div className="text-[10.5px] opacity-70 truncate">{n.tagline}</div>
            </div>
          </Link>
        ))}
      </div>
    </aside>
  );
}


// ─── Hero ──────────────────────────────────────────────────────────

function Hero({ data, onChange }) {
  const { identity, stats, relationship, is_owner } = data;
  const cover = identity.cover_image_url || identity.cover_url;
  const avatar = identity.avatar_url || identity.profile_picture;

  return (
    <section
      data-testid="member-hero"
      className="rounded-xl bg-white border overflow-hidden relative"
      style={{ borderColor: "#E5E7EB" }}
    >
      {/* Cover */}
      <div
        className="h-[220px] md:h-[280px] w-full bg-cover bg-center relative"
        style={{
          backgroundImage: cover
            ? `url(${cover})`
            : "linear-gradient(135deg, #FED7AA 0%, #FCA5A5 30%, #C7D2FE 70%, #6EE7B7 100%)",
        }}
      >
        {/* Featured latest article — turns the wasted banner into a
            first-touch conversion surface. */}
        {identity?.id && (
          <HeroLatestArticle
            authorId={identity.id}
            handle={data.handle}
            isOwner={is_owner}
            displayName={identity.name}
          />
        )}
        {/* Compact QR chip (bottom-right of banner). */}
        <QrPanel data={data} />
      </div>

      <div className="px-6 pb-6 pt-0">
        {/* Avatar */}
        <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-[64px]">
          <div className="relative shrink-0">
            <img
              src={avatar || "https://api.dicebear.com/7.x/initials/svg?seed=" + data.handle}
              alt={identity.name}
              className="w-[128px] h-[128px] rounded-full border-4 border-white object-cover shadow-md"
            />
            {is_owner && (
              <button
                data-testid="change-avatar-btn"
                className="absolute bottom-1.5 right-1.5 w-8 h-8 rounded-full bg-slate-900/80 flex items-center justify-center text-white"
              >
                <Camera className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex-1 md:pt-16 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl md:text-3xl leading-tight"
                  style={{ color: "#0F172A" }}
                  data-testid="member-name">
                {identity.name}
              </h1>
              {(identity.verification_tier && identity.verification_tier !== "none") && (
                <CheckCircle2 className="w-5 h-5" style={{ color: BRAND_BLUE }} data-testid="verified-badge" />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-[13px] mt-0.5">
              <span className="opacity-70">@{data.handle}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(16,185,129,0.10)", color: BRAND_GREEN }}>
                Active
              </span>
            </div>
            {(identity.city || identity.state || identity.country) && (
              <div className="mt-1 inline-flex items-center gap-1 text-[13px]" style={{ color: "#64748B" }}>
                <MapPin className="w-3.5 h-3.5" />
                {[identity.city, identity.state, identity.country].filter(Boolean).join(", ")}
              </div>
            )}
            {data.canonical_url && (
              <a href={data.canonical_url}
                 className="mt-0.5 inline-flex items-center gap-1 text-[13px]"
                 style={{ color: BRAND_BLUE }}>
                <LinkIcon className="w-3.5 h-3.5" />
                socialpulse.com/{data.handle}
              </a>
            )}
          </div>

          {/* Action buttons */}
          <div className="md:pt-16 flex flex-wrap gap-2 shrink-0">
            {is_owner
              ? <OwnerActions />
              : <VisitorActions
                    handle={data.handle}
                    relationship={relationship}
                    onChange={onChange}
                />
            }
          </div>
        </div>

        {/* Bio / headline */}
        {(identity.intro || identity.about) && (
          <p className="mt-4 text-[14.5px] leading-[1.55] max-w-[720px]"
             style={{ color: "#334155" }} data-testid="member-bio">
            {identity.intro || identity.about}
          </p>
        )}

        {/* Stats bar */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2 max-w-[560px]"
             data-testid="member-stats">
          <Stat n={stats.followers}      label="Followers" />
          <Stat n={stats.following}      label="Following" />
          <Stat n={stats.posts}          label="Posts" />
          <Stat n={stats.social_capital} label="Social Capital"
                highlight />
        </div>
      </div>
    </section>
  );
}


function Stat({ n, label, highlight = false }) {
  const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
  return (
    <div className="text-center">
      <div className="font-display text-[22px] leading-none"
           style={{ color: highlight ? BRAND_BLUE : "#0F172A" }}>
        {compact}
      </div>
      <div className="text-[11.5px] opacity-70 mt-1">{label}</div>
    </div>
  );
}


function OwnerActions() {
  return (
    <>
      <Link
        to="/settings"
        data-testid="edit-profile-btn"
        className="px-4 py-2 rounded-full text-[13px] font-medium border bg-white hover:bg-slate-50"
        style={{ borderColor: "#CBD5E1", color: "#0F172A" }}
      >
        Edit Profile
      </Link>
      <button
        data-testid="share-profile-btn"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-white"
        style={{ background: BRAND_PURPLE }}
      >
        <Share2 className="w-3.5 h-3.5" /> Share Profile
      </button>
    </>
  );
}


function VisitorActions({ handle, relationship, onChange }) {
  const [busy, setBusy] = useState(false);

  const call = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); await onChange(); } finally { setBusy(false); }
  };

  const follow = () => call(async () => {
    await axios.post(`${API}/follow/${handle}`, {}, { withCredentials: true });
    toast.success(`Following @${handle}`);
  });
  const unfollow = () => call(async () => {
    await axios.delete(`${API}/follow/${handle}`, { withCredentials: true });
  });
  const sendFriend = () => call(async () => {
    await axios.post(`${API}/friends/request/${handle}`, {}, { withCredentials: true });
    toast.success("Friend request sent");
  });
  const cancelFriend = () => call(async () => {
    await axios.post(`${API}/friends/cancel`,
                      { request_id: relationship.friendship.request_id },
                      { withCredentials: true });
  });
  const accept = () => call(async () => {
    await axios.post(`${API}/friends/accept`,
                      { request_id: relationship.friendship.request_id },
                      { withCredentials: true });
    toast.success(`You're now friends with @${handle}`);
  });
  const decline = () => call(async () => {
    await axios.post(`${API}/friends/decline`,
                      { request_id: relationship.friendship.request_id },
                      { withCredentials: true });
  });
  const unfriend = () => call(async () => {
    await axios.delete(`${API}/friends/${handle}`, { withCredentials: true });
  });

  const f = relationship.friendship;
  let friendBtn;
  if (!f) {
    friendBtn = (
      <button data-testid="add-friend-btn" onClick={sendFriend} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border bg-white hover:bg-slate-50"
              style={{ borderColor: "#CBD5E1", color: "#0F172A" }}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
        Add Friend
      </button>
    );
  } else if (f.status === "pending" && f.direction === "outgoing") {
    friendBtn = (
      <button data-testid="cancel-friend-btn" onClick={cancelFriend} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border bg-slate-50"
              style={{ borderColor: "#CBD5E1", color: "#64748B" }}>
        Request Sent
      </button>
    );
  } else if (f.status === "pending" && f.direction === "incoming") {
    friendBtn = (
      <>
        <button data-testid="accept-friend-btn" onClick={accept} disabled={busy}
                className="px-4 py-2 rounded-full text-[13px] font-semibold text-white"
                style={{ background: BRAND_GREEN }}>Accept</button>
        <button data-testid="decline-friend-btn" onClick={decline} disabled={busy}
                className="px-4 py-2 rounded-full text-[13px] font-medium border bg-white"
                style={{ borderColor: "#CBD5E1", color: "#64748B" }}>Decline</button>
      </>
    );
  } else if (f.status === "accepted") {
    friendBtn = (
      <button data-testid="unfriend-btn" onClick={unfriend} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border bg-white"
              style={{ borderColor: "#CBD5E1", color: "#0F172A" }}>
        <UserCheck className="w-3.5 h-3.5" style={{ color: BRAND_GREEN }} />
        Friends
      </button>
    );
  }

  const followBtn = relationship.is_following ? (
    <button data-testid="unfollow-btn" onClick={unfollow} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium border bg-white"
            style={{ borderColor: BRAND_PURPLE, color: BRAND_PURPLE }}>
      <UserCheck className="w-3.5 h-3.5" /> Following
    </button>
  ) : (
    <button data-testid="follow-btn" onClick={follow} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-white"
            style={{ background: BRAND_PURPLE }}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
      Follow
    </button>
  );

  return (
    <>
      {friendBtn}
      {followBtn}
      <button data-testid="message-btn"
              className="px-3 py-2 rounded-full text-[13px] font-medium border bg-white"
              style={{ borderColor: "#CBD5E1", color: "#0F172A" }}>
        <MessageCircle className="w-3.5 h-3.5" />
      </button>
      <SafetyMoreMenu handle={handle} canonicalUrl={window.location.href}
                      onChange={onChange} />
    </>
  );
}


function QrPanel({ data }) {
  const url = data.short_url || data.canonical_url || (typeof window !== "undefined" ? window.location.href : "");
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(url)}`;
  const label = (data.short_url || data.canonical_url || url || "").replace(/^https?:\/\//, "");
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    } catch {}
  };

  return (
    <div
      data-testid="qr-panel"
      className={`absolute right-3 bottom-3 rounded-xl bg-white/95 backdrop-blur border shadow-sm transition-all cursor-pointer ${
        expanded ? "p-3" : "p-1"
      }`}
      style={{ borderColor: "#E5E7EB" }}
      onClick={() => setExpanded((v) => !v)}
      title={expanded ? "Click to collapse" : "Scan to connect"}
    >
      <img
        src={qrSrc}
        alt="Profile QR"
        className={`transition-all ${expanded ? "w-[120px] h-[120px]" : "w-[48px] h-[48px]"}`}
      />
      {expanded && (
        <>
          <div className="text-center mt-2 text-[10.5px] font-medium" style={{ color: "#64748B" }}>
            Scan to connect
          </div>
          <button
            data-testid="copy-short-url"
            onClick={copy}
            className="flex items-center gap-1 justify-center mt-1.5 text-[11px] font-mono w-full"
            style={{ color: BRAND_PURPLE }}
          >
            <span className="truncate max-w-[120px]">{label}</span>
            <Copy className="w-3 h-3 shrink-0" />
          </button>
          {copied && <div className="text-[10px] text-center mt-0.5" style={{ color: BRAND_GREEN }}>Copied</div>}
        </>
      )}
    </div>
  );
}


// ─── Right rail ────────────────────────────────────────────────────

function RightRail({ data }) {
  return (
    <aside data-testid="member-right-rail" className="space-y-5">
      <SocialCapitalTile sc={data.social_capital} />
      <TopBadgesTile />
      <LinksTile links={data.links} isOwner={data.is_owner} />
      <MarketplaceTile marketplace={data.marketplace} isOwner={data.is_owner} />
      <EventsTile events={data.upcoming_events} isOwner={data.is_owner} />
    </aside>
  );
}


function SocialCapitalTile({ sc }) {
  const total = sc.total_points || 0;
  // Fake mini sparkline shape (placeholder for /timeline endpoint)
  const bars = Array.from({ length: 14 },
    (_, i) => 20 + Math.round(30 * Math.sin(i / 2) + 30));
  return (
    <div data-testid="sc-tile"
         className="rounded-xl bg-white border p-4"
         style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[15px]" style={{ color: "#0F172A" }}>
          Social Capital
        </h3>
        <Link to="/sc/me" className="text-[12px] font-medium" style={{ color: BRAND_BLUE }}>
          View all
        </Link>
      </div>
      <div className="flex items-end justify-between mb-1">
        <div className="font-display text-[36px] leading-none"
             style={{ color: BRAND_PURPLE }}>
          {total.toLocaleString()}
        </div>
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full"
              style={{ background: "rgba(124,58,237,0.10)", color: BRAND_PURPLE }}>
          <Sparkles className="w-3 h-3" />
        </span>
      </div>
      <div className="text-[11.5px] opacity-70 mb-3">
        Total Points · {sc.level || "Helper"}
      </div>
      <div className="flex items-end gap-0.5 h-[36px]">
        {bars.map((h, i) => (
          <div key={i} className="flex-1 rounded-t"
               style={{ background: BRAND_PURPLE, opacity: 0.15 + (i / bars.length) * 0.6, height: `${h}%` }} />
        ))}
      </div>
    </div>
  );
}


function TopBadgesTile() {
  // Phase B: read from /api/badges/{handle} once socialcapital.cc agent ships it.
  const stubs = [
    { icon: "🌱", label: "Nature Observer",  color: "#DCFCE7", ring: "#10B981" },
    { icon: "🍎", label: "Food Rescuer",     color: "#FEF3C7", ring: "#F59E0B" },
    { icon: "🤝", label: "Community Helper", color: "#DBEAFE", ring: "#3B82F6" },
    { icon: "🎓", label: "Knowledge Sharer", color: "#FEE2E2", ring: "#EF4444" },
  ];
  return (
    <div data-testid="badges-tile"
         className="rounded-xl bg-white border p-4"
         style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[15px]" style={{ color: "#0F172A" }}>Top Badges</h3>
        <Link to="/sc/me" className="text-[12px] font-medium" style={{ color: BRAND_BLUE }}>See all</Link>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {stubs.map((b) => (
          <div key={b.label} className="text-center">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-1 border-2"
                 style={{ background: b.color, borderColor: b.ring, fontSize: 22 }}>
              {b.icon}
            </div>
            <div className="text-[10.5px] leading-tight" style={{ color: "#475569" }}>
              {b.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function LinksTile({ links, isOwner }) {
  return (
    <div data-testid="links-tile"
         className="rounded-xl bg-white border p-4"
         style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-[15px]" style={{ color: "#0F172A" }}>My Links</h3>
        {links.length > 3 && (
          <button className="text-[12px] font-medium" style={{ color: BRAND_BLUE }}>See all</button>
        )}
      </div>
      {links.length === 0 ? (
        <div className="text-[12.5px] py-3 text-center rounded-lg border border-dashed"
             style={{ borderColor: "#E5E7EB", color: "#94A3B8" }}>
          {isOwner ? "Add your social links so people can find you elsewhere." : "No links added yet."}
        </div>
      ) : (
        <ul className="space-y-1">
          {links.slice(0, 5).map((l, i) => (
            <li key={i}>
              <a href={l.url} target="_blank" rel="noreferrer"
                 className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 text-[13px]">
                <span className="w-8 h-8 rounded-md inline-flex items-center justify-center"
                      style={{ background: "#EEF2FF", color: BRAND_BLUE }}>
                  <LinkIcon className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate" style={{ color: "#0F172A" }}>{l.label}</div>
                  <div className="text-[11px] opacity-70 truncate">{l.url.replace(/^https?:\/\//, "")}</div>
                </div>
                <ExternalLink className="w-3.5 h-3.5 opacity-50" />
              </a>
            </li>
          ))}
        </ul>
      )}
      {isOwner && (
        <button data-testid="add-link-btn"
                className="w-full mt-3 flex items-center justify-center gap-1.5 py-2 rounded-full text-[13px] font-medium text-white"
                style={{ background: BRAND_PURPLE }}>
          <Plus className="w-3.5 h-3.5" /> Add New Link
        </button>
      )}
    </div>
  );
}


function MarketplaceTile({ marketplace, isOwner }) {
  const items = marketplace.items || [];
  return (
    <div data-testid="marketplace-tile"
         className="rounded-xl bg-white border p-4"
         style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-[15px]" style={{ color: "#0F172A" }}>
          Marketplace{marketplace.active_count > 0 && <span className="opacity-60 font-normal"> ({marketplace.active_count} Active)</span>}
        </h3>
        {items.length > 0 && (
          <Link to="/local" className="text-[12px] font-medium" style={{ color: BRAND_BLUE }}>See all</Link>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-[12.5px] py-3 text-center rounded-lg border border-dashed"
             style={{ borderColor: "#E5E7EB", color: "#94A3B8" }}>
          {isOwner ? "Sell, give away, or offer something." : "No active listings."}
        </div>
      ) : (
        <ul className="space-y-1">
          {items.map((it) => (
            <li key={it.id}>
              <Link to={`/local/listings/${it.id}`}
                    className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50">
                <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                  {(it.hero_image || it.images?.[0]) ? (
                    <img src={it.hero_image || it.images?.[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Store className="w-4 h-4 opacity-40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-medium truncate" style={{ color: "#0F172A" }}>{it.title}</div>
                </div>
                <div className="text-[12px] font-semibold shrink-0"
                     style={{ color: it.price ? "#0F172A" : BRAND_GREEN }}>
                  {it.price ? `${it.currency || "$"}${it.price}` : "Free"}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {isOwner && (
        <Link to="/local/post"
              data-testid="post-listing-btn"
              className="w-full mt-3 flex items-center justify-center gap-1.5 py-2 rounded-full text-[13px] font-medium text-white"
              style={{ background: BRAND_PURPLE }}>
          <Plus className="w-3.5 h-3.5" /> Post New Listing
        </Link>
      )}
    </div>
  );
}


function EventsTile({ events, isOwner }) {
  return (
    <div data-testid="events-tile"
         className="rounded-xl bg-white border p-4"
         style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-[15px]" style={{ color: "#0F172A" }}>
          Upcoming Events
        </h3>
        {events.length > 0 && (
          <Link to="/events" className="text-[12px] font-medium" style={{ color: BRAND_BLUE }}>See all</Link>
        )}
      </div>
      {events.length === 0 ? (
        <div className="text-[12.5px] py-3 text-center rounded-lg border border-dashed"
             style={{ borderColor: "#E5E7EB", color: "#94A3B8" }}>
          {isOwner ? "Create an event so people can join." : "No upcoming events."}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {events.map((ev) => {
            const dt = ev.start_at ? new Date(ev.start_at) : null;
            const loc = [ev.city, ev.state, ev.country].filter(Boolean).join(", ");
            return (
              <li key={ev.id}>
                <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-slate-50">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md"
                        style={{ background: "rgba(59,130,246,0.10)", color: BRAND_BLUE }}>
                    <Calendar className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-medium truncate" style={{ color: "#0F172A" }}>{ev.title}</div>
                    <div className="text-[11px] opacity-70 truncate">
                      {dt ? dt.toLocaleDateString("en", { month: "short", day: "numeric" }) : ""}
                      {loc ? ` · ${loc}` : ""}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ─── Bottom quick-action bar ───────────────────────────────────────

function QuickActionBar({ isOwner }) {
  if (!isOwner) return null;
  const actions = [
    { icon: TrendingUp,  label: "Post an Update"  },
    { icon: Newspaper,   label: "Write an Article"},
    { icon: ImageIcon,   label: "Add Photos"      },
    { icon: VideoIcon,   label: "Share a Video"   },
    { icon: Calendar,    label: "Create Event"    },
    { icon: Handshake,   label: "Offer a Service" },
  ];
  return (
    <div
      data-testid="quick-action-bar"
      className="sticky bottom-0 border-t bg-white/95 backdrop-blur py-2 z-30"
      style={{ borderColor: "#E5E7EB" }}
    >
      <div className="max-w-[1400px] mx-auto px-4 flex flex-wrap justify-center gap-4">
        {actions.map((a) => (
          <button key={a.label}
                  className="inline-flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-full hover:bg-slate-50"
                  style={{ color: "#334155" }}>
            <a.icon className="w-3.5 h-3.5" style={{ color: BRAND_PURPLE }} />
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}
